<script setup>
import { PayCircleOutlined } from '@ant-design/icons-vue'
import Header from '@/components/Header'
import DesignFooter from '@/components/DesignFooter.vue'
import industry from '@/lang/cn/industry.js'
import { childItem } from './contant.js'

const data = childItem.openVend
const asset = (path) => `${import.meta.env.BASE_URL}${path}`
const heroImage = asset('image/solution-redesign/smart-scale-banner.png')
const heroDescription = '通过深度学习和AI视觉算法识别商品，完成称重、单价匹配和总价计算，适用于商超及生鲜零售。'
const painPoints = [
  { title: '结算效率低，影响消费体验', icon: 'inefficiency' },
  { title: '人力成本高企，侵蚀企业利润', icon: null },
  { title: '人为操作易出错，带来隐形损耗', icon: 'error' },
]
const overviewBlocks = [
  { key: 'recognition', html: industry.openVendScheme, image: 'OpenVending-1.jpg', alt: 'OpenVending AI商品识别与称重计价方案' },
  { key: 'speed', html: industry.openVendScheme2, image: 'OpenVending-2.jpg', alt: 'OpenVending AI智能识别秤模组部署方案' },
  { key: 'upgrade', html: industry.openVendScheme3 },
]
const scenes = data.pectorophonyApply.map((scene, index) => ({
  ...scene,
  title: index === 0 ? industry.supermarkets : industry.store,
  image: asset(`new-image/industry/OpenVending-${index + 4}.jpg`),
}))
const cases = data.caseImg.map((item) => ({
  ...item,
  image: asset('new-image/industry/OpenVending-3.png'),
}))
</script>

<template>
  <Header design-always hide-banner />
  <main class="smart-scale-page">
    <section class="solution-hero" aria-labelledby="smart-scale-title">
      <img :src="heroImage" class="hero-image" alt="" fetchpriority="high" />
      <div class="solution-container hero-content">
        <p class="hero-breadcrumb">解决方案 / {{ industry.openVend }}</p>
        <h1 id="smart-scale-title">{{ industry.openVend }}</h1>
        <p class="hero-description">{{ heroDescription }}</p>
      </div>
    </section>

    <section class="solution-section section-muted" aria-labelledby="smart-scale-background">
      <div class="solution-container">
        <h2 id="smart-scale-background">业务背景</h2>
        <!-- 仅使用仓库内可信中文静态文案，保留原文的段落分隔。 -->
        <div class="background-copy" v-html="industry.openVendBg" />
      </div>
    </section>

    <section class="solution-section" aria-labelledby="smart-scale-pain">
      <div class="solution-container">
        <h2 id="smart-scale-pain">行业痛点</h2>
        <div class="pain-grid">
          <article v-for="point in painPoints" :key="point.title" class="pain-card">
            <span class="pain-icon" aria-hidden="true">
              <img v-if="point.icon" :src="asset(`svg/${point.icon}.svg`)" alt="" width="24" height="24" loading="lazy" />
              <PayCircleOutlined v-else />
            </span>
            <h3>{{ point.title }}</h3>
          </article>
        </div>
      </div>
    </section>

    <section class="solution-section section-muted" aria-labelledby="smart-scale-overview">
      <div class="solution-container">
        <h2 id="smart-scale-overview">方案概述</h2>
        <div class="overview-stack">
          <template v-for="block in overviewBlocks" :key="block.key">
            <!-- HTML 仅来自本地语言包，不接受接口或用户输入。 -->
            <div class="rich-copy" v-html="block.html" />
            <img v-if="block.image" :src="asset(`new-image/industry/${block.image}`)" :alt="block.alt" class="overview-image" width="1920" height="1067" loading="lazy" decoding="async" />
          </template>
        </div>
      </div>
    </section>

    <section class="solution-section" aria-labelledby="smart-scale-scenes">
      <div class="solution-container">
        <h2 id="smart-scale-scenes">应用场景</h2>
        <p class="section-description">按使用场景和设备条件选择能力。</p>
        <div class="scene-grid">
          <article v-for="scene in scenes" :key="scene.title" class="scene-card">
            <img :src="scene.image" :alt="scene.title" class="scene-image" loading="lazy" decoding="async" />
            <div class="scene-body">
              <h3>{{ scene.title }}</h3>
              <p>{{ scene.dec }}</p>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="solution-section section-muted" aria-labelledby="smart-scale-cases">
      <div class="solution-container">
        <h2 id="smart-scale-cases">相关案例</h2>
        <div class="case-grid">
          <a v-for="item in cases" :key="item.link" :href="item.link" class="case-card" v-track="{ position: 'solution > case', page: 'smart-scale', case: item.title || item.name || item.link.split('?')[0] }" target="_blank" rel="noopener noreferrer">
            <img :src="item.image" alt="" class="case-image" loading="lazy" decoding="async" />
            <div class="case-body">{{ item.dec }}</div>
          </a>
        </div>
      </div>
    </section>
  </main>
  <DesignFooter />
</template>

<style scoped>
.smart-scale-page { min-width: 0; color: #14233b; background: #fff; font-size: 15px; line-height: 29px; }
.smart-scale-page, .smart-scale-page *, .smart-scale-page *::before, .smart-scale-page *::after { box-sizing: border-box; }
.solution-container { width: min(1440px, calc(100% - 48px)); margin-inline: auto; }
.smart-scale-page h1, .smart-scale-page h2, .smart-scale-page h3, .smart-scale-page p { margin: 0; }
.smart-scale-page h2 { margin-bottom: 24px; color: #14233b; font-size: 24px; font-weight: 500; line-height: 1.4; text-align: center; }
.smart-scale-page h3 { color: #14233b; font-size: 18px; font-weight: 500; line-height: 1.5; }
.solution-section { padding-block: 64px; }
.section-muted { background: #f4f7fb; }
.solution-hero { position: relative; display: flex; align-items: center; min-height: 352px; padding-block: 64px; color: #fff; background: #0d182b; }
.hero-image { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.hero-content { position: relative; }
.hero-breadcrumb { margin-bottom: 8px !important; font-size: 14px; line-height: 24px; }
.hero-content h1 { margin-bottom: 16px; color: #fff; font-size: 30px; font-weight: 600; line-height: 1.35; overflow-wrap: anywhere; }
.hero-description { font-size: 16px; line-height: 1.5; }
.background-copy { color: #617089; text-align: center; }
.pain-grid, .scene-grid, .case-grid { display: grid; gap: 24px; align-items: stretch; }
.pain-grid, .case-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.case-grid:has(> .case-card:only-child) { grid-template-columns: minmax(0, 1fr); width: calc((100% - 48px) / 3); margin-inline: auto; }
/* 两张场景整组居中，桌面仍保持每张占三列中的一列。 */
.scene-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); width: calc((100% - 48px) / 3 * 2 + 24px); margin-inline: auto; }
.pain-card, .scene-card, .case-card { min-width: 0; border: 1px solid #dce2ea; border-radius: 16px; background: #fff; }
.pain-card { display: flex; align-items: center; gap: 16px; padding: 24px; }
.pain-icon { display: flex; flex: 0 0 40px; align-items: center; justify-content: center; width: 40px; height: 40px; border-radius: 50%; background: #f4f7fb; color: #14233b; font-size: 24px; }
.pain-icon img { display: block; width: 24px; height: 24px; }
.overview-stack { display: flex; flex-direction: column; gap: 24px; }
.rich-copy { color: #617089; overflow-wrap: break-word; }
.rich-copy :deep(h3) { margin: 0; color: #14233b !important; font-size: 18px; font-weight: 500; line-height: 1.5; }
.overview-image { display: block; width: 100%; height: auto; border-radius: 16px; }
.section-description { margin: -8px 0 24px !important; color: #617089; text-align: center; }
.scene-card, .case-card { display: flex; flex-direction: column; overflow: hidden; height: 100%; }
.scene-image { display: block; width: 100%; aspect-ratio: 16 / 9; object-fit: contain; background: #fff; }
.scene-body { flex: 1; padding: 24px; }
.scene-body h3 { margin-bottom: 8px; }
.scene-body p { color: #617089; font-size: 14px; line-height: 24px; }
.case-card { color: #14233b; text-decoration: none; transition: border-color 160ms ease, box-shadow 160ms ease; }
.case-image { display: block; width: 100%; aspect-ratio: 464 / 196; object-fit: contain; background: #fff; }
.case-body { flex: 1; padding: 24px; font-size: 16px; line-height: 24px; }
.case-card:hover { color: #14233b; border-color: #9baabd; box-shadow: 0 8px 24px rgb(20 35 59 / 8%); }
.case-card:focus-visible { outline: 3px solid #1478d4; outline-offset: 4px; }
@media (min-width: 768px) {
  .smart-scale-page h2 { font-size: 26px; }
  .hero-content h1 { font-size: 36px; }
  .hero-description { font-size: 18px; }
}
@media (min-width: 992px) {
  .smart-scale-page h2 { font-size: 28px; }
  .hero-content h1 { font-size: 40px; }
  .hero-description { font-size: 18px; }
}
@media (min-width: 1200px) {
  .smart-scale-page h2 { font-size: 30px; }
  .hero-content h1 { font-size: 44px; }
  .hero-description { font-size: 18px; }
}
@media (min-width: 1400px) {
  .smart-scale-page h2 { font-size: 32px; }
}
@media (max-width: 991px) {
  .pain-grid, .scene-grid, .case-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .scene-grid { width: 100%; }
  .case-grid:has(> .case-card:only-child) { width: calc((100% - 24px) / 2); }
}
@media (max-width: 767px) {
  .solution-container { width: calc(100% - 32px); }
  .solution-section { padding-block: 40px; }
  .solution-hero { min-height: 280px; padding-block: 40px; }
  .pain-grid, .scene-grid, .case-grid { grid-template-columns: minmax(0, 1fr); }
  .case-grid:has(> .case-card:only-child) { width: 100%; }
}
@media (prefers-reduced-motion: reduce) {
  .case-card { transition: none; }
}
</style>